import type { Passenger } from '@full-elevator/sim';
import { PEOPLE } from '../theme.js';

/**
 * Who is holding each ticket (ADR-0018), as numbers — no drawing here, so the
 * sizes and the looks can be tested without a renderer.
 *
 * The ticket is today's chip and carries every rule. The person around it only
 * says who they are, and has to fit on floors that are 40–126px tall depending
 * on the level and the phone, against a ticket that stays 30px whatever happens.
 *
 * So there is one person, stretched to the floor (v0.8, the owner's option B
 * on the design canvas's "Kısa katlarda insan" page). `stretch` 1 is the whole
 * figure the mockups drew: head well above the ticket, legs below it, feet on
 * the slab. 0 is the same person compact: a smaller head close over the
 * ticket, short legs under it. Every floor in between gets something in
 * between, and a little below 0 the head and legs shrink a touch further
 * before the ticket is left alone.
 *
 * It replaced three fixed sizes (whole, bust, ticket) that were measured on a
 * 375×812 phone. In Safari on an iPhone, half the levels came out as bare
 * tickets, and only two had legs. Now every level does there. Cabin riders are
 * always tickets: the cabin is smaller than any floor.
 */
export interface Build {
  /** 1: the whole figure. 0: compact. Slightly below 0 on the shortest floors that fit anyone. */
  readonly stretch: number;
  /**
   * A VIP's top hat crown, in head units: as tall as the floor allows, up to the
   * mockup's 15. Never under 7, below which it stops reading as a top hat.
   */
  readonly crown: number;
}

/**
 * The figures are drawn on the mockup's grid (`design/HotelE.dc.html`): a 28-unit
 * ticket whose top edge is at y 42, so one unit is `size / 28` pixels.
 */
export const TICKET_TOP_UNITS = 42;
/** The slab every storey stands on (`ShaftView.drawBands`). */
const SLAB = 2;
/** The top of the tallest hair, above the head's centre, in head units (radius 8). */
const HAIR_TOP_UNITS = 13;
/** Hair may brush the floor above by this much, in px: it reads as standing tall. */
const HEADROOM_GRACE = 1;
/** How far below compact a person may shrink before only the ticket fits. */
export const MIN_STRETCH = -0.25;
/** A top hat may reach this far into the floor above, in px (KNOWN_ISSUES). */
export const HAT_GRACE = 4;
const CROWN = { min: 7, max: 15 } as const;
/** The top hat's brim sits this far above the head's centre, in head units. */
const BRIM_UNITS = 9;

/** Whole and compact, in grid units: how far the head sinks, how big it is, where the feet are. */
const WHOLE = { headDrop: 0, headScale: 1, feet: 41.2, shoulders: 36 } as const;
const COMPACT = { headDrop: 12, headScale: 0.68, feet: 33.6, shoulders: 40.6 } as const;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function unit(size: number): number {
  return size / 28;
}

/** Where a person's shoes touch the ground, below the ticket's top, in units. */
export function feetUnits(build: Pick<Build, 'stretch'>): number {
  return lerp(COMPACT.feet, WHOLE.feet, build.stretch);
}

/** The top of the shoulders, in units: tucked behind the ticket when compact. */
export function shouldersUnits(build: Pick<Build, 'stretch'>): number {
  return lerp(COMPACT.shoulders, WHOLE.shoulders, Math.max(0, build.stretch));
}

/** Head centre and radius on the mockup grid, for an ordinary and a broad-built person. */
export function headCentreUnits(
  wide: boolean,
  build: Pick<Build, 'stretch'>,
): { x: number; y: number; r: number } {
  const base = wide ? { x: 30, y: 25, r: 9.5 } : { x: 18, y: 26, r: 8 };
  // The head never sinks further than compact: below that it only shrinks.
  const drop = lerp(COMPACT.headDrop, WHOLE.headDrop, Math.max(0, build.stretch));
  const scale = lerp(COMPACT.headScale, WHOLE.headScale, build.stretch);
  return { x: base.x, y: base.y + drop, r: base.r * scale };
}

/** How tall a person of this build stands, hair to shoes, in px. */
export function standingHeight(build: Pick<Build, 'stretch'>, size: number): number {
  const head = headCentreUnits(false, build);
  const hairTop = head.y - (HAIR_TOP_UNITS * head.r) / 8;
  return (TICKET_TOP_UNITS - hairTop + feetUnits(build)) * unit(size);
}

/** The most person a floor this tall has room for, or null for the ticket alone. */
export function buildFor(floorHeight: number, size: number): Build | null {
  const room = floorHeight - SLAB + HEADROOM_GRACE;
  if (standingHeight({ stretch: MIN_STRETCH }, size) > room) return null;
  let stretch = 1;
  if (standingHeight({ stretch }, size) > room) {
    // Height grows with stretch, so the tallest build that fits is found by halving.
    let low = MIN_STRETCH;
    let high = 1;
    for (let i = 0; i < 20; i += 1) {
      const mid = (low + high) / 2;
      if (standingHeight({ stretch: mid }, size) <= room) low = mid;
      else high = mid;
    }
    stretch = low;
  }
  // The hat gets what headroom is left above the head, and a little of the floor above.
  const k = unit(size);
  const head = headCentreUnits(false, { stretch });
  const floorTop = TICKET_TOP_UNITS - (floorHeight - SLAB) / k + feetUnits({ stretch });
  const room8 = (head.y - (floorTop - HAT_GRACE / k)) / (head.r / 8) - BRIM_UNITS;
  return { stretch, crown: Math.min(CROWN.max, Math.max(CROWN.min, room8)) };
}

/** Where the top of a VIP's hat is, relative to the ticket's top, in px. */
export function hatTop(build: Build, size: number): number {
  const head = headCentreUnits(false, build);
  const top = head.y - (BRIM_UNITS + build.crown) * (head.r / 8);
  return (top - TICKET_TOP_UNITS) * unit(size);
}

/** Y of the ticket's top edge on a floor: the person's feet on the slab, or a bare ticket centred. */
export function ticketTop(
  build: Build | null,
  floorCentre: number,
  floorHeight: number,
  size: number,
): number {
  if (!build) return floorCentre - size / 2;
  return floorCentre + floorHeight / 2 - SLAB - feetUnits(build) * unit(size);
}

/**
 * The top of a bare head, relative to the ticket's top: where an arrow pointing
 * at somebody should stop. Zero for a ticket on its own. A hat sits higher.
 */
export function headTop(build: Build | null, size: number): number {
  if (!build) return 0;
  const head = headCentreUnits(false, build);
  return (head.y - (HAIR_TOP_UNITS * head.r) / 8 - TICKET_TOP_UNITS) * unit(size);
}

export type HairStyle = 'bald' | 'short' | 'bun' | 'long' | 'curly';
const HAIR_STYLES: readonly HairStyle[] = ['short', 'short', 'bun', 'long', 'curly', 'bald'];

export interface Look {
  readonly skin: number;
  readonly hair: HairStyle;
  readonly hairColour: number;
  readonly coat: number;
  readonly glasses: boolean;
}

/**
 * The same person every time they are drawn, from nothing but their id.
 *
 * Presentation only: the sim knows nothing about faces and must not, so this
 * hashes the id rather than asking the seeded generator for anything. Ids are
 * sequential (`p0`, `p1`, …), which is why the hash is mixed — neighbours in a
 * queue should not come out as near-twins.
 */
export function lookOf(passenger: Pick<Passenger, 'id' | 'vip'>): Look {
  const h = mix(fnv1a(passenger.id));
  return {
    skin: pick(PEOPLE.skin, h),
    hair: pick(HAIR_STYLES, h >>> 4),
    hairColour: pick(PEOPLE.hair, h >>> 8),
    // A VIP is always in the dark suit: the costume is how they are recognised.
    coat: passenger.vip ? PEOPLE.suit : pick(PEOPLE.coats, h >>> 12),
    glasses: ((h >>> 16) & 7) === 0,
  };
}

function pick<T>(list: readonly T[], bits: number): T {
  return list[(bits >>> 0) % list.length] as T;
}

function fnv1a(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** MurmurHash3's finaliser: every input bit moves every output bit. */
function mix(value: number): number {
  let h = value;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * What a passenger wears, if not their own clothes: an emergency crew
 * (ADR-0029) or a building's own people (ADR-0031). The costume follows from
 * it, and it is recognition only — every rule is on the ticket.
 */
export type Uniform =
  | 'medic'
  | 'police'
  | 'postman'
  | 'housekeeping'
  | 'commuter'
  | 'cleaner'
  | 'waiter'
  | 'bellhop'
  | 'cyclist'
  | 'executive'
  | 'investor'
  | 'traveller'
  | 'robe'
  | 'wedding';

const UNIFORMS: Readonly<Record<string, Uniform>> = {
  paramedic: 'medic',
  stretcher: 'medic',
  police: 'police',
  arrest: 'police',
  postman: 'postman',
  housekeeping: 'housekeeping',
  commuter: 'commuter',
  windowCleaner: 'cleaner',
  windowCleanerDown: 'cleaner',
  // More of each building's own (ADR-0043).
  roomService: 'waiter',
  bellhop: 'bellhop',
  cyclist: 'cyclist',
  ceo: 'executive',
  investor: 'investor',
  // The hotel's departures (ADR-0051).
  shuttleGuest: 'traveller',
  breakfastGuest: 'robe',
  newlywed: 'wedding',
};

export function uniformOf(passenger: Pick<Passenger, 'archetype'>): Uniform | null {
  return UNIFORMS[passenger.archetype] ?? null;
}

/** Which crew a passenger belongs to, if any (ADR-0029): the emergency's lights follow from it. */
export type Crew = 'medic' | 'police' | null;

export function crewOf(passenger: Pick<Passenger, 'archetype'>): Crew {
  const uniform = uniformOf(passenger);
  return uniform === 'medic' || uniform === 'police' ? uniform : null;
}

/** What each uniform's coat is, in the game and on the DOM screens alike. */
export const UNIFORM_COAT: Record<Uniform, number> = {
  medic: PEOPLE.medic,
  police: PEOPLE.police,
  postman: PEOPLE.postal,
  housekeeping: PEOPLE.housekeeping,
  commuter: PEOPLE.commuter,
  cleaner: PEOPLE.overalls,
  waiter: PEOPLE.waiter,
  bellhop: PEOPLE.bellhop,
  cyclist: PEOPLE.cyclist,
  executive: PEOPLE.suit,
  investor: PEOPLE.investor,
  traveller: PEOPLE.traveller,
  robe: PEOPLE.robe,
  wedding: PEOPLE.wedding,
};

/** A cap or a hard hat over the hair. */
export function hidesHair(passenger: Pick<Passenger, 'archetype'>): boolean {
  const uniform = uniformOf(passenger);
  return (
    uniform === 'police' ||
    uniform === 'postman' ||
    uniform === 'cleaner' ||
    uniform === 'bellhop' ||
    uniform === 'cyclist'
  );
}

/**
 * A courier, as drawn: a deadline and nothing more urgent. A crew carries a
 * deadline too (ADR-0029), but wears its uniform, not the courier's helmet.
 */
export function isCourier(
  passenger: Pick<Passenger, 'deadlineMs' | 'critical' | 'archetype'>,
): boolean {
  // A commuter runs for a train (ADR-0031): a deadline, but no helmet.
  return passenger.deadlineMs !== null && !passenger.critical && uniformOf(passenger) === null;
}
